// Mock data for container management
// This file provides mock data and responses for development and testing

import { 
  Container, 
  ContainerFilters,
  ContainerPaginationParams,
  ContainerFormData,
  ContainerBatchOperation,
  ContainerBatchResult,
  ContainerSpec,
  ContainerLocation,
  L4Code,
  ApiResponse,
  ContainerUsageStatus,
  ContainerStatus,
  ContainerOccupancyStatus
} from '../types/container';
import { PaginatedResponse } from '../types/index';
import { 
  createSuccessResponse, 
  createErrorResponse, 
  createPaginatedResponse 
} from '../utils/responseTransformer';

/**
 * Mock container data with various states and occupancy scenarios
 * Includes enabled/disabled containers and occupied/available containers
 */
export const mockContainers: Container[] = [
  // Enabled and Available containers
  {
    code: '250726.0001',
    name: 'Shoppingcart 001',
    usageStatus: 'ENABLE' as ContainerUsageStatus,
    status: 'ENABLE' as ContainerStatus,
    containerSpecCode: 'SC',
    containerSpecName: 'Shoppingcart',
    containerSpecType: 'STANDARD',
    locationCode: 'WH001',
    category: 'STORAGE',
    purposeType: 'GENERAL',
    l4Code: 'L4001',
    gmtCreate: Date.now() - 86400000 * 30, // 30 days ago
    gmtModified: Date.now() - 86400000 * 5, // 5 days ago
    creatorName: 'John Smith',
    modifierName: 'Jane Doe',
    remark: 'Standard shopping cart, good condition'
  },
  {
    code: '250726.0002',
    name: 'Shoppingcart 002',
    usageStatus: 'ENABLE' as ContainerUsageStatus,
    status: 'ENABLE' as ContainerStatus,
    containerSpecCode: 'RK',
    containerSpecName: 'Shopping Cart',
    containerSpecType: 'STANDARD',
    locationCode: 'WH001',
    category: 'STORAGE',
    purposeType: 'GENERAL',
    l4Code: 'L4001',
    gmtCreate: Date.now() - 86400000 * 25,
    gmtModified: Date.now() - 86400000 * 3,
    creatorName: 'Mike Johnson',
    modifierName: 'Sarah Wilson'
  },
  
  // Enabled and Occupied containers
  {
    code: '250726.0003',
    name: 'Shoppingcart 003',
    usageStatus: 'ENABLE' as ContainerUsageStatus,
    status: 'ENABLE' as ContainerStatus,
    occupantName: 'Operator A',
    occupantAccount: 'operator_a',
    occupyTaskCode: 'TASK001',
    occupyTaskType: 'PICKING',
    occupyTaskTypeLabel: 'Picking Task',
    containerSpecCode: 'RK',
    containerSpecName: 'Shopping Cart',
    containerSpecType: 'STANDARD',
    locationCode: 'WH002',
    category: 'STORAGE',
    purposeType: 'PICKING',
    l4Code: 'L4002',
    gmtCreate: Date.now() - 86400000 * 20,
    gmtModified: Date.now() - 86400000 * 1,
    creatorName: 'John Smith',
    modifierName: 'Operator A',
    remark: 'Currently executing picking task'
  },
  {
    code: '250726.0004',
    name: 'Shoppingcart 004',
    usageStatus: 'ENABLE' as ContainerUsageStatus,
    status: 'ENABLE' as ContainerStatus,
    occupantName: 'Operator B',
    occupantAccount: 'operator_b',
    occupyTaskCode: 'TASK002',
    occupyTaskType: 'REPLENISHMENT',
    occupyTaskTypeLabel: 'Replenishment Task',
    containerSpecCode: 'SC',
    containerSpecName: 'Shoppingcart',
    containerSpecType: 'STANDARD',
    locationCode: 'ST001',
    category: 'STAGING',
    purposeType: 'REPLENISHMENT',
    l4Code: 'L4003',
    gmtCreate: Date.now() - 86400000 * 15,
    gmtModified: Date.now() - 3600000 * 2, // 2 hours ago
    creatorName: 'Jane Doe',
    modifierName: 'Operator B',
    remark: 'Replenishment task in progress'
  },
  
  // Disabled containers
  {
    code: '250726.0005',
    name: 'Shoppingcart 005',
    usageStatus: 'DISABLE' as ContainerUsageStatus,
    status: 'DISABLE' as ContainerStatus,
    containerSpecCode: 'SC',
    containerSpecName: 'Shoppingcart',
    containerSpecType: 'STANDARD',
    locationCode: 'WH001',
    category: 'STORAGE',
    purposeType: 'MAINTENANCE',
    l4Code: 'L4001',
    gmtCreate: Date.now() - 86400000 * 45,
    gmtModified: Date.now() - 86400000 * 7,
    creatorName: 'Mike Johnson',
    modifierName: 'Maintenance Staff',
    remark: 'Cart damaged, temporarily out of service'
  },
  {
    code: '250726.0006',
    name: 'Shoppingcart 006',
    usageStatus: 'DISABLE' as ContainerUsageStatus,
    status: 'DISABLE' as ContainerStatus,
    containerSpecCode: 'SC',
    containerSpecName: 'Shoppingcart',
    containerSpecType: 'STANDARD',
    locationCode: 'WH002',
    category: 'STORAGE',
    purposeType: 'MAINTENANCE',
    l4Code: 'L4002',
    gmtCreate: Date.now() - 86400000 * 40,
    gmtModified: Date.now() - 86400000 * 10,
    creatorName: 'Sarah Wilson',
    modifierName: 'Maintenance Staff',
    remark: 'Under routine maintenance'
  },
  
  // Small containers
  {
    code: '250726.0007',
    name: 'Rollcage 001',
    usageStatus: 'ENABLE' as ContainerUsageStatus,
    status: 'ENABLE' as ContainerStatus,
    containerSpecCode: 'RC',
    containerSpecName: 'Rollcage',
    containerSpecType: 'LARGE',
    locationCode: 'WH001',
    category: 'STORAGE',
    purposeType: 'SPECIAL',
    l4Code: 'L4001',
    gmtCreate: Date.now() - 86400000 * 10,
    gmtModified: Date.now() - 86400000 * 2,
    creatorName: 'John Smith',
    modifierName: 'Jane Doe',
    remark: 'Dedicated for small items'
  },
  {
    code: '250726.0008',
    name: 'Rollcage 002',
    usageStatus: 'ENABLE' as ContainerUsageStatus,
    status: 'ENABLE' as ContainerStatus,
    occupantName: 'Operator C',
    occupantAccount: 'operator_c',
    occupyTaskCode: 'TASK003',
    occupyTaskType: 'SORTING',
    occupyTaskTypeLabel: 'Sorting Task',
    containerSpecCode: 'RC',
    containerSpecName: 'Rollcage',
    containerSpecType: 'LARGE',
    locationCode: 'ST001',
    category: 'STAGING',
    purposeType: 'SORTING',
    l4Code: 'L4003',
    gmtCreate: Date.now() - 86400000 * 8,
    gmtModified: Date.now() - 3600000 * 1, // 1 hour ago
    creatorName: 'Mike Johnson',
    modifierName: 'Operator C',
    remark: 'Sorting operation in progress'
  },
  
  // Large containers
  {
    code: '250726.0009',
    name: 'DIY 001',
    usageStatus: 'ENABLE' as ContainerUsageStatus,
    status: 'ENABLE' as ContainerStatus,
    containerSpecCode: 'DIY',
    containerSpecName: 'DIY',
    containerSpecType: 'CUSTOM',
    locationCode: 'WH002',
    category: 'STORAGE',
    purposeType: 'BULK',
    l4Code: 'L4002',
    gmtCreate: Date.now() - 86400000 * 12,
    gmtModified: Date.now() - 86400000 * 1,
    creatorName: 'Sarah Wilson',
    modifierName: 'John Smith',
    remark: 'For bulk item storage'
  },
  {
    code: '250726.0010',
    name: 'DIY 002',
    usageStatus: 'DISABLE' as ContainerUsageStatus,
    status: 'DISABLE' as ContainerStatus,
    containerSpecCode: 'DIY',
    containerSpecName: 'DIY',
    containerSpecType: 'CUSTOM',
    locationCode: 'WH002',
    category: 'STORAGE',
    purposeType: 'MAINTENANCE',
    l4Code: 'L4002',
    gmtCreate: Date.now() - 86400000 * 35,
    gmtModified: Date.now() - 86400000 * 14,
    creatorName: 'Jane Doe',
    modifierName: 'Maintenance Staff',
    remark: 'Under weight capacity inspection'
  },
  
  // Additional containers for pagination testing
  ...Array.from({ length: 90 }, (_, index) => ({
    code: `250726.${String(index + 11).padStart(4, '0')}`,
    name: `Container ${String(index + 11).padStart(3, '0')}`,
    usageStatus: (index % 3 === 0 ? 'DISABLE' : 'ENABLE') as ContainerUsageStatus,
    status: (index % 4 === 0 ? 'DISABLE' : 'ENABLE') as ContainerStatus,
    occupantName: index % 5 === 0 ? `Operator ${String.fromCharCode(65 + (index % 26))}` : undefined,
    occupantAccount: index % 5 === 0 ? `operator_${String.fromCharCode(97 + (index % 26))}` : undefined,
    occupyTaskCode: index % 5 === 0 ? `TASK${String(index + 100).padStart(3, '0')}` : undefined,
    occupyTaskType: index % 5 === 0 ? ['PICKING', 'REPLENISHMENT', 'SORTING', 'INVENTORY'][index % 4] : undefined,
    occupyTaskTypeLabel: index % 5 === 0 ? ['Picking Task', 'Replenishment Task', 'Sorting Task', 'Inventory Task'][index % 4] : undefined,
    containerSpecCode: ['SC', 'RC', 'DIY'][index % 3],
    containerSpecName: ['Shoppingcart', 'Rollcage', 'DIY'][index % 3],
    containerSpecType: ['STANDARD', 'LARGE', 'CUSTOM'][index % 3],
    locationCode: ['WH001', 'WH002', 'ST001'][index % 3],
    category: ['STORAGE', 'STAGING'][index % 2],
    purposeType: ['GENERAL', 'SPECIAL', 'BULK', 'MAINTENANCE'][index % 4],
    l4Code: ['L4001', 'L4002', 'L4003'][index % 3],
    gmtCreate: Date.now() - 86400000 * (index + 1),
    gmtModified: Date.now() - 86400000 * Math.max(1, Math.floor(index / 10)),
    creatorName: ['John Smith', 'Jane Doe', 'Mike Johnson', 'Sarah Wilson'][index % 4],
    modifierName: ['John Smith', 'Jane Doe', 'Mike Johnson', 'Sarah Wilson', 'Maintenance Staff', 'Operator A'][index % 6],
    remark: index % 10 === 0 ? `Batch generated test container ${index + 11}` : undefined
  }))
];

/**
 * Mock container specifications
 */
export const mockContainerSpecs: ContainerSpec[] = [
  { code: 'RC', name: 'Rollcage', type: 'LARGE' },
  { code: 'DIY', name: 'DIY', type: 'CUSTOM' },
  { code: 'RK', name: 'Shopping Cart', type: 'STANDARD' },
  { code: 'BX', name: 'Basket', type: 'STANDARD' },
  { code: 'PL', name: 'Pallet', type: 'LARGE' },
  { code: 'TB', name: 'Tote', type: 'STANDARD' },
];

/**
 * Mock container locations
 */
export const mockContainerLocations: ContainerLocation[] = [
  { code: 'WH001', name: 'Warehouse Zone A', type: 'WAREHOUSE' },
  { code: 'WH002', name: 'Warehouse Zone B', type: 'WAREHOUSE' },
  { code: 'ST001', name: 'Staging Area 1', type: 'STAGING' },
];

/**
 * Mock L4 codes
 */
export const mockL4Codes: L4Code[] = [
  { code: 'L4001', name: 'L4 Zone 1' },
  { code: 'L4002', name: 'L4 Zone 2' },
  { code: 'L4003', name: 'L4 Zone 3' },
];

/**
 * Generate mock API response for container list with filtering and pagination
 * @param filters - Filter criteria
 * @param pagination - Pagination parameters
 * @returns Mock API response
 */
export const getMockContainerListResponse = (
  filters: ContainerFilters = {},
  pagination: ContainerPaginationParams = { currentPage: 1, pageSize: 100 }
): ApiResponse<PaginatedResponse<Container>> => {
  try {
    // Apply filters
    let filteredContainers = mockContainers.filter(container => {
      // Filter by usage status
      if (filters.usageStatus && filters.usageStatus !== 'all') {
        if (container.usageStatus !== filters.usageStatus) return false;
      }
      
      // Filter by status
      if (filters.status && filters.status !== 'all') {
        if (container.status !== filters.status) return false;
      }
      
      // Filter by occupancy status
      if (filters.occupancyStatus && filters.occupancyStatus !== 'all') {
        const isOccupied = !!(container.occupantName || container.occupyTaskCode);
        const occupancyStatus: ContainerOccupancyStatus = isOccupied ? 'OCCUPIED' : 'AVAILABLE';
        if (occupancyStatus !== filters.occupancyStatus) return false;
      }
      
      // Filter by L4 code
      if (filters.l4Code && container.l4Code !== filters.l4Code) {
        return false;
      }
      
      // Filter by container spec code
      if (filters.containerSpecCode && container.containerSpecCode !== filters.containerSpecCode) {
        return false;
      }
      
      // Filter by location code
      if (filters.locationCode && container.locationCode !== filters.locationCode) {
        return false;
      }
      
      // Search by code or name
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const codeMatch = container.code.toLowerCase().includes(searchTerm);
        const nameMatch = container.name.toLowerCase().includes(searchTerm);
        if (!codeMatch && !nameMatch) return false;
      }
      
      return true;
    });
    
    // Apply sorting if specified
    if (pagination.sortBy) {
      filteredContainers.sort((a, b) => {
        const field = pagination.sortBy!;
        const order = pagination.sortOrder || 'asc';
        
        let aValue: any = a[field as keyof Container];
        let bValue: any = b[field as keyof Container];
        
        // Handle undefined values
        if (aValue === undefined) aValue = '';
        if (bValue === undefined) bValue = '';
        
        // Handle different data types
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        let comparison = 0;
        if (aValue < bValue) comparison = -1;
        if (aValue > bValue) comparison = 1;
        
        return order === 'desc' ? -comparison : comparison;
      });
    }
    
    // Apply pagination
    const totalCount = filteredContainers.length;
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    const paginatedContainers = filteredContainers.slice(startIndex, endIndex);
    
    return createPaginatedResponse(
      paginatedContainers,
      pagination.currentPage,
      pagination.pageSize,
      totalCount
    );
  } catch (error) {
    return createErrorResponse('获取托盘列表失败', 'MOCK_ERROR');
  }
};

/**
 * Generate mock API response for container detail
 * @param code - Container code
 * @returns Mock API response
 */
export const getMockContainerDetailResponse = (code: string): ApiResponse<Container> => {
  try {
    const container = mockContainers.find(c => c.code === code);
    if (!container) {
      return createErrorResponse('托盘不存在', 'NOT_FOUND');
    }
    return createSuccessResponse(container);
  } catch (error) {
    return createErrorResponse('获取托盘详情失败', 'MOCK_ERROR');
  }
};

/**
 * Generate mock API response for container creation
 * @param containerData - Container form data
 * @returns Mock API response
 */
export const getMockContainerCreateResponse = (containerData: ContainerFormData): ApiResponse<Container> => {
  try {
    // Generate new container code
    const existingCodes = mockContainers.map(c => c.code);
    let newCode: string;
    let counter = 1;
    
    do {
      newCode = `250726.${String(counter).padStart(4, '0')}`;
      counter++;
    } while (existingCodes.includes(newCode));
    
    // Create new container
    const newContainer: Container = {
      code: newCode,
      name: newCode,
      usageStatus: "ENABLE",
      status: "ENABLE",
      gmtCreate: Date.now(),
      gmtModified: Date.now(),
      creatorName: '当前用户',
      modifierName: '当前用户'
    };
    
    // Add to mock data
    mockContainers.unshift(newContainer);
    
    return createSuccessResponse(newContainer);
  } catch (error) {
    return createErrorResponse('创建托盘失败', 'CREATE_ERROR');
  }
};

/**
 * Generate mock API response for batch update
 * @param operation - Batch operation details
 * @returns Mock API response
 */
export const getMockContainerBatchUpdateResponse = (operation: ContainerBatchOperation): ApiResponse<ContainerBatchResult> => {
  try {
    const result: ContainerBatchResult = {
      successCount: 0,
      failureCount: 0,
      failures: []
    };
    
    for (const code of operation.codes) {
      const containerIndex = mockContainers.findIndex(c => c.code === code);
      
      if (containerIndex === -1) {
        result.failureCount++;
        result.failures.push({
          code,
          reason: '托盘不存在'
        });
        continue;
      }
      
      const container = mockContainers[containerIndex];
      
      // Check if container is occupied for disable operation
      if (operation.operation === 'DISABLE' && (container.occupantName || container.occupyTaskCode)) {
        result.failureCount++;
        result.failures.push({
          code,
          reason: 'Container is currently in use and cannot be disabled'
        });
        continue;
      }
      
      // Update container status
      const newStatus: ContainerUsageStatus = operation.operation === 'ENABLE' ? 'ENABLE' : 'DISABLE';
      mockContainers[containerIndex] = {
        ...container,
        usageStatus: newStatus,
        status: newStatus,
        gmtModified: Date.now(),
        modifierName: 'Current User'
      };
      
      result.successCount++;
    }
    
    return createSuccessResponse(result);
  } catch (error) {
    return createErrorResponse('批量操作失败', 'BATCH_ERROR');
  }
};

/**
 * Generate mock API response for container specs
 * @returns Mock API response
 */
export const getMockContainerSpecsResponse = (): ApiResponse<ContainerSpec[]> => {
  return createSuccessResponse(mockContainerSpecs);
};

/**
 * Generate mock API response for container locations
 * @returns Mock API response
 */
export const getMockContainerLocationsResponse = (): ApiResponse<ContainerLocation[]> => {
  return createSuccessResponse(mockContainerLocations);
};

/**
 * Generate mock API response for L4 codes
 * @returns Mock API response
 */
export const getMockL4CodesResponse = (): ApiResponse<L4Code[]> => {
  return createSuccessResponse(mockL4Codes);
};

/**
 * Refresh mock container data (reset to initial state)
 * This function reloads the original mock data, useful for testing
 */
export const refreshMockContainerData = (): void => {
  // Clear current data
  mockContainers.length = 0;
  
  // Reload original data by re-executing the mock data generation
  // This is a simplified approach - in a real scenario, you might want to 
  // store the original data separately and restore from it
  const originalData = generateOriginalMockData();
  mockContainers.push(...originalData);
  
  console.log('Mock container data refreshed with', mockContainers.length, 'containers');
};

/**
 * Generate the original mock data set
 * @returns Array of original container data
 */
function generateOriginalMockData(): Container[] {
  // This would contain the same data as defined above
  // For simplicity, we'll just return the current mockContainers
  // In a real implementation, you might want to store this separately
  return [
    // The same data structure as defined in mockContainers above
    // This is a placeholder - in practice you'd want to separate this
  ];
}

/**
 * Validate container code uniqueness in mock data
 * @param code - Container code to validate
 * @param excludeCode - Optional code to exclude from validation (for updates)
 * @returns True if code is unique/valid
 */
export const validateMockContainerCode = (code: string, excludeCode?: string): boolean => {
  if (!code || code.trim() === '') {
    return false;
  }
  
  // Check if code already exists (excluding the specified code for updates)
  const existingContainer = mockContainers.find(c => 
    c.code === code && c.code !== excludeCode
  );
  
  return !existingContainer;
};

/**
 * Get container statistics from mock data
 * @returns Statistics about containers
 */
export const getMockContainerStats = () => {
  const total = mockContainers.length;
  const enabled = mockContainers.filter(c => c.usageStatus === 'ENABLE').length;
  const disabled = mockContainers.filter(c => c.usageStatus === 'DISABLE').length;
  const occupied = mockContainers.filter(c => c.occupantName || c.occupyTaskCode).length;
  const available = total - occupied;
  
  return {
    total,
    enabled,
    disabled,
    occupied,
    available,
    bySpec: mockContainerSpecs.map(spec => ({
      spec: spec.code,
      name: spec.name,
      count: mockContainers.filter(c => c.containerSpecCode === spec.code).length
    })),
    byLocation: mockContainerLocations.map(location => ({
      location: location.code,
      name: location.name,
      count: mockContainers.filter(c => c.locationCode === location.code).length
    }))
  };
};

/**
 * Simulate network delay for mock responses
 * @param minMs - Minimum delay in milliseconds
 * @param maxMs - Maximum delay in milliseconds
 * @returns Promise that resolves after the delay
 */
export const simulateNetworkDelay = (minMs: number = 200, maxMs: number = 800): Promise<void> => {
  const delay = Math.random() * (maxMs - minMs) + minMs;
  return new Promise(resolve => setTimeout(resolve, delay));
};